import React from 'react';

import { createTimeoutState } from 'utils/react';
import PageLoader from 'ui/PageLoader';

import {
    loadConnectors,
    installConnector,
    removeConnector,
    updateConnector,
    resetConnector,
    synchronizeConnector,
} from '../Services';
import { loadCompany } from '../company/services';

import { ViewForm, EditForm, DetailsForm } from './common/Forms';
import { ConnectorDashboard } from './common/Dashboard';
import { CreateNewConfirm, DeleteConfirm } from './common/Dialogs';
import { withCompanyContext } from '../CompanyContext';
import ConnectorTable from './common/Table';

import ActiveDirectoryConfig from './active_directory/config';
import AzureActiveDirectoryConfig from './azure_active_directory/config';
import { ActionTypes } from './Consts';

export const CONNECTORS = [ActiveDirectoryConfig, AzureActiveDirectoryConfig];

const findConfig = identifier =>
    CONNECTORS.find(connector => connector.identifier === identifier);

function replaceConnector(connectors, newConnector) {
    return connectors.map(connector => {
        if (connector.uuid === newConnector.uuid) {
            return { ...connector, ...newConnector };
        }
        return connector;
    });
}

class ConnectorsTab extends React.Component {
    state = {
        action: ActionTypes.NONE,
        actionPayload: {},
        loaded: false,
        loading: true,
        instaling: [],
        installed: [],
        connectors: [],
        organization: {},
    };
    constructor() {
        super();
        this.setChange = createTimeoutState(this.setState.bind(this));
    }
    onAction = async (actionName, payload = {}) => {
        const { history, company } = this.props;
        const { identifier } = payload;
        switch (actionName) {
            case ActionTypes.SAVE:
                const { extra, rules } = payload;
                await this.updateConnector(identifier, rules, extra);
                this.resetAction();
                break;
            case ActionTypes.RELOAD:
                this.resetAction();
                await this.loadConnectors();
                break;
            case ActionTypes.INSTALL:
                this.resetAction();
                await this.installConnector(identifier);
                break;
            case ActionTypes.REMOVE_CONFIRMED:
                this.resetAction();
                await this.removeConnector(identifier);
                await this.loadConnectors();
                break;
            case ActionTypes.REMOVE:
                this.setAction(ActionTypes.REMOVE, { identifier });
                break;
            case ActionTypes.EDIT:
                this.setAction(ActionTypes.EDIT, { identifier });
                break;
            case ActionTypes.DETAILS:
                this.setAction(ActionTypes.DETAILS, { identifier });
                break;
            case ActionTypes.CREATE_NEW:
                this.setAction(ActionTypes.CREATE_NEW, { identifier });
                break;
            case ActionTypes.CREATE_NEW_CONFIRMED:
                this.resetAction();
                await this.resetConnector(identifier);
                break;
            case ActionTypes.SYNCHRONIZE:
                await this.synchronizeConnector(identifier);
                break;
            case ActionTypes.RESET:
                this.resetAction();
                break;
            case ActionTypes.REDIRECT:
                history.push(`/companies/${company.id}/settings`);
                break;
            default:
                break;
        }
    };
    async installConnector(identifier) {
        const { company } = this.props;
        const { connectors } = this.state;
        await this.tryInstaling(identifier, async () => {
            const connector = await installConnector(company.id, identifier);
            const installed = [...this.state.installed, connector.identifier];
            this.setAction(ActionTypes.VIEW, { identifier });
            this.setChange({
                installed,
                connectors: connectors.concat([connector]),
            });
        });
    }
    async removeConnector(identifier) {
        const { company } = this.props;
        await this.tryLoading(async () => {
            const connector = this.getConnector(identifier);
            await removeConnector(company.id, connector.uuid);
        });
    }
    async loadConnectors() {
        const { company } = this.props;
        await this.tryLoading(async () => {
            const connectors = await loadConnectors(company.id);
            this.setupConnectors(connectors);
        });
    }
    async loadCompany() {
        const { company } = this.props;
        const organization = await loadCompany(company.id);
        this.setState({ organization });
    }
    async updateConnector(identifier, rules, extra) {
        const { company } = this.props;
        await this.tryLoading(async () => {
            const { uuid } = this.getConnector(identifier);
            const newConnector = await updateConnector(
                company.id,
                uuid,
                rules,
                extra
            );
            const connectors = replaceConnector(
                this.state.connectors,
                newConnector
            );
            this.setChange({ connectors });
        });
    }
    async resetConnector(identifier) {
        const { company } = this.props;
        await this.tryLoading(async () => {
            const { uuid } = this.getConnector(identifier);
            const connector = await resetConnector(company.id, uuid);
            const connectors = replaceConnector(
                this.state.connectors,
                connector
            );
            this.setChange({ connectors }, () => {
                this.setAction(ActionTypes.VIEW, { identifier });
            });
        });
    }
    async synchronizeConnector(identifier) {
        const { company } = this.props;
        await this.tryLoading(async () => {
            const { uuid } = this.getConnector(identifier);
            await synchronizeConnector(company.id, uuid);
        });
    }
    setupConnectors(connectors) {
        const installed = connectors.map(connector => connector.identifier);
        this.setChange({
            loaded: true,
            installed,
            connectors,
        });
    }
    async tryInstaling(identifier, callback) {
        this.addInstaling(identifier);
        try {
            await callback();
        } catch (err) {
            console.warn(err);
        } finally {
            this.removeInstaling(identifier);
        }
    }
    addInstaling(identifier) {
        this.setChange({
            loading: true,
            instaling: [...this.state.instaling, identifier],
        });
    }
    removeInstaling(identifier) {
        const instaling = this.state.instaling.filter(
            item => item !== identifier
        );
        this.setChange({
            loading: false,
            instaling,
        });
    }
    async tryLoading(callback) {
        this.setChange({
            loading: true,
        });
        try {
            await callback();
        } catch (err) {
            console.warn(err);
        } finally {
            this.setChange({
                loading: false,
            });
        }
    }
    setAction(action, actionPayload) {
        this.setChange({
            action,
            actionPayload,
        });
    }
    resetAction = () => {
        this.setChange({
            action: ActionTypes.NONE,
            actionPayload: {},
        });
    };
    async componentDidMount() {
        await this.tryLoading(async () => {
            await this.loadCompany();
            await this.loadConnectors();
        });
    }
    render() {
        const {
            action,
            actionPayload,
            installed,
            instaling,
            loading,
            loaded,
            connectors,
            organization,
        } = this.state;
        const { editable } = this.props;
        if (!loaded) {
            return <PageLoader />;
        }
        if (action === ActionTypes.VIEW) {
            const { identifier } = actionPayload;
            const config = findConfig(identifier);
            const connector = this.getConnector(identifier);
            return (
                <ViewForm
                    config={config}
                    connector={connector}
                    onAction={this.onAction}
                />
            );
        }
        if (action === ActionTypes.EDIT) {
            const { identifier } = actionPayload;
            const config = findConfig(identifier);
            const connector = this.getConnector(identifier);
            return (
                <EditForm
                    config={config}
                    connector={connector}
                    onAction={this.onAction}
                />
            );
        }
        if (action === ActionTypes.DETAILS) {
            const { identifier } = actionPayload;
            const config = findConfig(identifier);
            const connector = this.getConnector(identifier);
            return (
                <DetailsForm
                    config={config}
                    connector={connector}
                    onAction={this.onAction}
                />
            );
        }
        return (
            <React.Fragment>
                {this.renderAction()}
                {loaded && (
                    <ConnectorTable
                        loading={loading}
                        connectors={connectors}
                        editable={editable}
                        onAction={this.onAction}
                    />
                )}
                {loaded && (
                    <ConnectorDashboard
                        installed={installed}
                        instaling={instaling}
                        editable={editable}
                        connectors={CONNECTORS}
                        allowed={organization.subscription_level === 'advanced'}
                        onAction={this.onAction}
                    />
                )}
            </React.Fragment>
        );
    }
    renderAction() {
        const { action, actionPayload } = this.state;
        if (action === ActionTypes.REMOVE) {
            const { identifier } = actionPayload;
            const config = findConfig(identifier);
            return (
                <DeleteConfirm
                    name={config.name}
                    onAction={type => this.onAction(type, { identifier })}
                />
            );
        }
        if (action === ActionTypes.CREATE_NEW) {
            const { identifier } = actionPayload;
            const config = findConfig(identifier);
            return (
                <CreateNewConfirm
                    name={config.name}
                    onAction={type => this.onAction(type, { identifier })}
                />
            );
        }
        return null;
    }
    getConnector(identifier) {
        return this.state.connectors.find(
            connector => connector.identifier === identifier
        );
    }
}

export default withCompanyContext(ConnectorsTab);
